import React, { useEffect, useRef, useState } from 'react';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { store } from '../../store';
import { useAppSelector } from '../../store/hooks';
import { adminApiService } from '../../services/adminApiService';
import { branding } from '../../constants/branding';
import SimpleStrategyConfig from './SimpleStrategyConfig';
import AdminTradingStrategies from './AdminTradingStrategies';
import AdminAnalyticsTabs from './AdminAnalyticsTabs';

type Bot = {
  id: number;
  name: string;
  symbol: string;
  timeframe: string;
  strategy: string;
  maxNotionalUsd: number;
  status: string;
};

type BotMetrics = {
  botId: number;
  status: string;
  runId?: number;
  startedAt?: number;
  portfolioValue: number;
  realizedPnl: number;
  peakDrawdown: number;
  tradingStats: {
    totalTrades: number;
    winningTrades: number;
    losingTrades: number;
    winRate: number;
    totalFees: number;
    totalVolume: number;
  };
  riskMetrics: {
    currentDrawdownPct: number;
    maxDrawdownPct: number;
  };
};

type ExchangeData = {
  symbol: string;
  last: number;
  change: number;
  percentage: number;
  volume: number;
  high: number;
  low: number;
};

const TradingBotsPanel: React.FC = () => {
  const adminPermissions: string[] = useAppSelector((state: any) => state?.auth?.adminUser?.permissions || []);
  const hasGranularTabPermission =
    adminPermissions.includes('VIEW_ADMIN_TRADING_BOTS_TAB') ||
    adminPermissions.includes('VIEW_ADMIN_TRADING_STRATEGIES_TAB');
  // Granular sub-tab permissions take precedence when present.
  // Legacy VIEW_ADMIN_TRADING_BOTS acts as both-tabs only when no granular tabs are assigned.
  const canViewBotsTab = hasGranularTabPermission
    ? adminPermissions.includes('VIEW_ADMIN_TRADING_BOTS_TAB')
    : adminPermissions.includes('VIEW_ADMIN_TRADING_BOTS');
  const canViewStrategiesTab = hasGranularTabPermission
    ? adminPermissions.includes('VIEW_ADMIN_TRADING_STRATEGIES_TAB')
    : adminPermissions.includes('VIEW_ADMIN_TRADING_BOTS');

  const [bots, setBots] = useState<Bot[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ 
    name: '', 
    symbol: 'BTC/USDT', 
    timeframe: '1h', 
    strategy: 'MA_CROSS', 
    maxNotionalUsd: 1000, 
    defaultMode: 'PAPER' as 'PAPER'|'LIVE'|'SHADOW',
    // Hybrid strategy parameters
    primaryStrategy: 'MA_CROSS',
    secondaryStrategy: 'RSI'
  });
  // Initialize activeTab from localStorage, default to 'bots'
  const [activeTab, setActiveTab] = useState<'bots' | 'strategies'>('bots');
  const [botDetailTab, setBotDetailTab] = useState<'overview' | 'metrics' | 'trades' | 'risk' | 'strategy' | 'crypto-wallets' | 'users'>('overview');
  const [error, setError] = useState<string | null>(null);
  const [selectedBot, setSelectedBot] = useState<number | null>(null);
  const [liveStatus, setLiveStatus] = useState<any | null>(null);
  const [botMetrics, setBotMetrics] = useState<BotMetrics | null>(null);
  const [exchangeData, setExchangeData] = useState<ExchangeData | null>(null);
  // Always start with no modal open — restoring from localStorage caused a re-render loop
  // that prevented the tradingBots tab from loading (tab would snap back to dashboard).
  const [showAnalytics, setShowAnalytics] = useState<{ type: 'bots' | 'strategies', id: number } | null>(null);
  const esRef = useRef<EventSource | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Clear any stale persisted modal state on mount so old state never auto-restores
  useEffect(() => {
    localStorage.removeItem('tradingBotsPanelShowAnalytics');
  }, []);

  // Crypto wallet state
  const [botCryptoAllocations, setBotCryptoAllocations] = useState<any[]>([]);
  const [usersData, setUsersData] = useState<any[]>([]);

  // Save showAnalytics to localStorage whenever it changes
  useEffect(() => {
    if (showAnalytics) {
      localStorage.setItem('tradingBotsPanelShowAnalytics', JSON.stringify(showAnalytics));
      
      // On mobile, scroll to top when modal opens
      if (window.innerWidth <= 768) {
        // Function to perform all scroll operations
        const performScroll = () => {
          // Scroll window and document
          window.scrollTo(0, 0);
          if (document.documentElement) {
            document.documentElement.scrollTop = 0;
          }
          if (document.body) {
            document.body.scrollTop = 0;
          }
          
          // Scroll the admin-content container (this is the scrollable container on mobile)
          const adminContent = document.querySelector('.admin-content') as HTMLElement;
          if (adminContent) {
            adminContent.scrollTop = 0;
            adminContent.scrollTo(0, 0);
          }
          // Also try scrolling the admin-main if it exists
          const adminMain = document.querySelector('.admin-main') as HTMLElement;
          if (adminMain) {
            adminMain.scrollTop = 0;
          }
          // Try scrolling admin-content-body as well
          const adminContentBody = document.querySelector('.admin-content-body') as HTMLElement;
          if (adminContentBody) {
            adminContentBody.scrollTop = 0;
          }
        };
        
        // Perform scroll immediately
        performScroll();
        
        // Also perform scroll after render using double RAF to ensure DOM is ready
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            performScroll();
          });
        });
        
        // Prevent body scroll while modal is open
        document.body.classList.add('modal-open');
      }
    } else {
      // Only clear if explicitly set to null (not on initial mount)
      localStorage.removeItem('tradingBotsPanelShowAnalytics');
      
      // Restore body scroll when modal closes
      if (window.innerWidth <= 768) {
        document.body.classList.remove('modal-open');
      }
    }
    
    // Cleanup: restore body scroll on unmount
    return () => {
      if (window.innerWidth <= 768) {
        document.body.classList.remove('modal-open');
      }
    };
  }, [showAnalytics]);

  // Debug: Log showAnalytics state changes
  useEffect(() => {
    console.log('TradingBotsPanel: showAnalytics state:', showAnalytics);
  }, [showAnalytics]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Popular cryptocurrency trading pairs
  const tradingPairs = [
    'BTC/USDT', 'ETH/USDT', 'BNB/USDT', 'ADA/USDT', 'SOL/USDT',
    'XRP/USDT', 'DOT/USDT', 'DOGE/USDT', 'AVAX/USDT', 'MATIC/USDT',
    'LINK/USDT', 'UNI/USDT', 'LTC/USDT', 'BCH/USDT', 'ATOM/USDT',
    'FTM/USDT', 'NEAR/USDT', 'ALGO/USDT', 'VET/USDT', 'ICP/USDT',
    'FIL/USDT', 'TRX/USDT', 'ETC/USDT', 'XLM/USDT', 'HBAR/USDT',
    'BTC/BUSD', 'ETH/BUSD', 'BNB/BUSD', 'ADA/BUSD', 'SOL/BUSD',
    'BTC/USDC', 'ETH/USDC', 'BNB/USDC', 'ADA/USDC', 'SOL/USDC',
    'BTC/ETH', 'ETH/BNB', 'ADA/ETH', 'SOL/ETH', 'DOT/ETH'
  ];

  const fetchBots = async () => {
    setLoading(true);
    const res = await adminApiService.getTradingBots();
    if (res.success && res.data) setBots(res.data as any);
    setLoading(false);
  };

  // Crypto wallet functions
  const fetchBotCryptoAllocations = async (botId: number) => {
    try {
      const response = await adminApiService.getBotCryptoAllocations(botId);
      if (response.success) {
        setBotCryptoAllocations(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch bot crypto allocations:', error);
    }
  };

  const fetchBotMetrics = async (botId: number) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const state = store.getState();
      const token = state.auth.token;
      const authType = state.auth.type;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token && authType === 'JWT') headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${apiBaseUrl}/api/trading/bots/${botId}/metrics`, { 
        credentials: 'include', 
        headers 
      });
      if (response.ok) {
        const result = await response.json();
        // Handle both direct metrics object and wrapped response
        const metrics = result.data || result;
        setBotMetrics(metrics);
      }
    } catch (err) {
      console.error('Failed to fetch bot metrics:', err);
    }
  };

  const fetchExchangeData = async (symbol: string) => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const state = store.getState();
      const token = state.auth.token;
      const authType = state.auth.type;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token && authType === 'JWT') headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${apiBaseUrl}/api/trading/bots/exchange/ticker/${symbol}`, { 
        credentials: 'include', 
        headers 
      });
      if (response.ok) {
        const result = await response.json();
        // Handle both direct ticker object and wrapped response
        const ticker = result.data || result;
        setExchangeData({
          symbol: ticker.symbol || symbol,
          last: ticker.last || 0,
          change: ticker.change || 0,
          percentage: ticker.percentage || 0,
          volume: ticker.baseVolume || 0,
          high: ticker.high || ticker.last || 0,
          low: ticker.low || ticker.last || 0
        });
      }
    } catch (err) {
      console.error('Failed to fetch exchange data:', err);
    }
  };

  // Save activeTab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('tradingBotsPanelActiveTab', activeTab);
  }, [activeTab]);

  // Resolve initial/next allowed tab based on permissions and localStorage.
  useEffect(() => {
    const savedSubTab = localStorage.getItem('tradingBotsPanelActiveTab');
    const savedAllowed =
      (savedSubTab === 'bots' && canViewBotsTab) ||
      (savedSubTab === 'strategies' && canViewStrategiesTab);

    if (savedAllowed) {
      setActiveTab(savedSubTab as 'bots' | 'strategies');
      return;
    }

    if (canViewBotsTab) {
      setActiveTab('bots');
      return;
    }

    if (canViewStrategiesTab) {
      setActiveTab('strategies');
    }
  }, [canViewBotsTab, canViewStrategiesTab]);

  useEffect(() => { 
    fetchBots(); 
  }, []);

  // Ensure scroll container is properly initialized on mobile
  useEffect(() => {
    if (isMobile) {
      // Force scroll container to recalculate height after a brief delay
      // This ensures scrolling works immediately even if content is still loading
      const timer = setTimeout(() => {
        const scrollContainer = document.querySelector('.admin-content') as HTMLElement;
        if (scrollContainer) {
          // Trigger a reflow to ensure browser calculates scroll height
          scrollContainer.scrollTop = scrollContainer.scrollTop;
          // Force layout recalculation
          void scrollContainer.offsetHeight;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isMobile, activeTab]);

  useEffect(() => {
    if (selectedBot) {
      fetchBotMetrics(selectedBot);
      fetchBotCryptoAllocations(selectedBot);
      const bot = bots.find(b => b.id === selectedBot);
      if (bot) {
        fetchExchangeData(bot.symbol);
      }
    }
  }, [selectedBot, bots]);

  // Fetch retail users using this bot (Admin analytics users endpoint)
  useEffect(() => {
    const fetchUsersForBot = async () => {
      if (!selectedBot || botDetailTab !== 'users') return;
      try {
        const apiBaseUrl = getApiBaseUrl();
        const state = store.getState();
        const token = state.auth.token;
        const authType = state.auth.type;
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token && authType === 'JWT') headers['Authorization'] = `Bearer ${token}`;
        const resp = await fetch(`${apiBaseUrl}/api/admin/analytics/bots/${selectedBot}/users?range=1M&page=0&size=50&sort=totalPnl&direction=desc`, { credentials: 'include', headers });
        if (!resp.ok) { setUsersData([]); return; }
        const data = await resp.json();
        setUsersData((data.status === 'success' || data.status === 'Successful') ? (data.data || []) : []);
      } catch (e) {
        console.error('Failed to fetch bot users:', e);
        setUsersData([]);
      }
    };
    fetchUsersForBot();
  }, [botDetailTab, selectedBot]);

  useEffect(() => {
    if (selectedBot == null) {
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
      setLiveStatus(null);
      return;
    }
    const url = adminApiService.getTradingBotStatusStreamUrl(selectedBot);
    const es = new EventSource(url, { withCredentials: true });
    const handler = (e: MessageEvent) => {
      try {
        const parsed = JSON.parse(e.data);
        setLiveStatus(parsed);
      } catch {
        setLiveStatus(e.data as any);
      }
    };
    // The server emits named events: "status". Listen to that specifically.
    es.addEventListener('status', handler as EventListener);
    // Also fallback to generic message if server omits the name
    es.onmessage = handler;
    esRef.current = es;
    return () => { es.removeEventListener('status', handler as EventListener); es.close(); };
  }, [selectedBot]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Prepare bot data - only include hybrid strategy parameters if strategy is HYBRID
    const botData: any = {
      name: form.name,
      symbol: form.symbol,
      timeframe: form.timeframe,
      strategy: form.strategy,
      maxNotionalUsd: form.maxNotionalUsd,
      defaultMode: form.defaultMode
    };
    
    // Only add hybrid strategy parameters if strategy is HYBRID
    if (form.strategy === 'HYBRID') {
      botData.primaryStrategy = form.primaryStrategy;
      botData.secondaryStrategy = form.secondaryStrategy;
    }
    
    const res = await adminApiService.createTradingBot(botData);
    if (!res.success) { setError(res.error || 'Failed'); return; }
    setForm({ name: '', symbol: 'BTC/USDT', timeframe: '1h', strategy: 'MA_CROSS', maxNotionalUsd: 1000, defaultMode: 'PAPER', primaryStrategy: 'MA_CROSS', secondaryStrategy: 'RSI' });
    fetchBots();
  };

  const handleStart = async (botId: number) => {
    await adminApiService.startTradingBot(botId, 'PAPER');
    fetchBots();
  };

  const handleStop = async (botId: number) => {
    await adminApiService.stopTradingBot(botId);
    fetchBots();
  };

  return (
    <div>
      <div className="admin-content-header">
        <h2 className="content-title">Trading Bots</h2>
        <div className="content-breadcrumb">Admin Dashboard / Trading Bots</div>
      </div>

      {/* Tab Navigation */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${branding.borderColor}`, marginBottom: 24 }}>
        {canViewBotsTab && (
          <button
            onClick={() => setActiveTab('bots')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'bots' ? branding.primaryColor : 'transparent',
              color: activeTab === 'bots' ? '#fff' : branding.textColor,
              cursor: 'pointer',
              borderBottom: activeTab === 'bots' ? `2px solid ${branding.primaryColor}` : '2px solid transparent',
              fontWeight: activeTab === 'bots' ? '600' : '400'
            }}
          >
            Trading Bots
          </button>
        )}
        {canViewStrategiesTab && (
          <button
            onClick={() => setActiveTab('strategies')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'strategies' ? branding.primaryColor : 'transparent',
              color: activeTab === 'strategies' ? '#fff' : branding.textColor,
              cursor: 'pointer',
              borderBottom: activeTab === 'strategies' ? `2px solid ${branding.primaryColor}` : '2px solid transparent',
              fontWeight: activeTab === 'strategies' ? '600' : '400'
            }}
          >
            Trading Strategies
          </button>
        )}
      </div>

      {/* Tab Content */}
      {!canViewBotsTab && !canViewStrategiesTab ? (
        <div
          style={{
            border: `1px solid ${branding.borderColor}`,
            borderRadius: 8,
            background: '#fff',
            padding: 24,
            color: branding.textColor
          }}
        >
          You don't have permission to view Trading Bots or Trading Strategies.
        </div>
      ) : activeTab === 'bots' && canViewBotsTab ? (
        <div className="admin-content-body" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', gap: 24 }}>
        {/* Create Bot Form */}
        <form onSubmit={handleCreate} style={{ background: '#fff', border: `1px solid ${branding.borderColor}`, borderRadius: 8, padding: 16 }}>
          <h3 style={{ marginTop: 0 }}>Create Bot</h3>
          <div style={{ display: 'grid', gap: 16 }}>
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Bot Name *</label>
              <input 
                placeholder="Enter a descriptive name for your bot" 
                value={form.name} 
                onChange={e => setForm({ ...form, name: e.target.value })} 
                required 
                style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
              />
              <small style={{ color: '#666', fontSize: 12 }}>Choose a unique name to identify this trading bot</small>
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Trading Pair *</label>
              <select 
                value={form.symbol} 
                onChange={e => setForm({ ...form, symbol: e.target.value })} 
                required
                style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
              >
                {tradingPairs.map(pair => (
                  <option key={pair} value={pair}>{pair}</option>
                ))}
              </select>
              <small style={{ color: '#666', fontSize: 12 }}>Select the cryptocurrency pair this bot will trade</small>
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Timeframe *</label>
              <select 
                value={form.timeframe} 
                onChange={e => setForm({ ...form, timeframe: e.target.value })}
                required
                style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
              >
                <option value="1m">1 Minute</option>
                <option value="5m">5 Minutes</option>
                <option value="15m">15 Minutes</option>
                <option value="30m">30 Minutes</option>
                <option value="1h">1 Hour</option>
                <option value="4h">4 Hours</option>
                <option value="1d">1 Day</option>
                <option value="1w">1 Week</option>
              </select>
              <small style={{ color: '#666', fontSize: 12 }}>Candlestick interval for market analysis (1h recommended for beginners)</small>
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Strategy *</label>
              <select 
                value={form.strategy} 
                onChange={e => setForm({ ...form, strategy: e.target.value })}
                required
                style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
              >
                <option value="MA_CROSS">Moving Average Crossover</option>
                <option value="RSI">RSI Mean Reversion</option>
                <option value="BOLLINGER">Bollinger Bands</option>
                <option value="MACD">MACD (Moving Average Convergence Divergence)</option>
                <option value="STOCHASTIC">Stochastic Oscillator</option>
                <option value="ICHIMOKU">Ichimoku Cloud</option>
                <option value="PARABOLIC_SAR">Parabolic SAR</option>
                <option value="VWAP">VWAP (Volume Weighted Average Price)</option>
                <option value="ATR">ATR (Average True Range)</option>
                <option value="HYBRID">Hybrid Strategy</option>
              </select>
              <small style={{ color: '#666', fontSize: 12 }}>Trading algorithm that determines buy/sell signals (10 strategies available)</small>
            </div>

            {/* Hybrid Strategy Configuration */}
            {form.strategy === 'HYBRID' && (
              <>
                <div className="form-group">
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Primary Strategy *</label>
                  <select 
                    value={form.primaryStrategy} 
                    onChange={e => setForm({ ...form, primaryStrategy: e.target.value })}
                    required
                    style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
                  >
                    <option value="MA_CROSS">Moving Average Crossover</option>
                    <option value="RSI">RSI Mean Reversion</option>
                    <option value="BOLLINGER">Bollinger Bands</option>
                    <option value="MACD">MACD (Moving Average Convergence Divergence)</option>
                    <option value="STOCHASTIC">Stochastic Oscillator</option>
                    <option value="ICHIMOKU">Ichimoku Cloud</option>
                    <option value="PARABOLIC_SAR">Parabolic SAR</option>
                    <option value="VWAP">VWAP (Volume Weighted Average Price)</option>
                    <option value="ATR">ATR (Average True Range)</option>
                  </select>
                  <small style={{ color: '#666', fontSize: 12 }}>Main strategy that will have higher weight in signal generation</small>
                </div>

                <div className="form-group">
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Secondary Strategy *</label>
                  <select 
                    value={form.secondaryStrategy} 
                    onChange={e => setForm({ ...form, secondaryStrategy: e.target.value })}
                    required
                    style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
                  >
                    <option value="MA_CROSS">Moving Average Crossover</option>
                    <option value="RSI">RSI Mean Reversion</option>
                    <option value="BOLLINGER">Bollinger Bands</option>
                    <option value="MACD">MACD (Moving Average Convergence Divergence)</option>
                    <option value="STOCHASTIC">Stochastic Oscillator</option>
                    <option value="ICHIMOKU">Ichimoku Cloud</option>
                    <option value="PARABOLIC_SAR">Parabolic SAR</option>
                    <option value="VWAP">VWAP (Volume Weighted Average Price)</option>
                    <option value="ATR">ATR (Average True Range)</option>
                  </select>
                  <small style={{ color: '#666', fontSize: 12 }}>Confirmation strategy that must agree with primary for signals</small>
                </div>

                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#e3f2fd', 
                  border: '1px solid #2196f3', 
                  borderRadius: '4px', 
                  marginBottom: '16px' 
                }}>
                  <strong>Hybrid Strategy Info:</strong>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                    <li>Signals are generated only when both strategies agree on direction</li>
                    <li>Primary strategy has 70% weight, secondary has 30% weight by default</li>
                    <li>You can adjust weights and confirmation threshold in the Strategy tab after creation</li>
                  </ul>
                </div>
              </>
            )}

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Initial Capital (USD) *</label>
              <input 
                type="number" 
                placeholder="Enter amount in USD" 
                value={form.maxNotionalUsd} 
                onChange={e => setForm({ ...form, maxNotionalUsd: Number(e.target.value) })} 
                required 
                min="1"
                max="1000000"
                style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
              />
              <small style={{ color: '#666', fontSize: 12 }}>Maximum amount this bot can trade (minimum $1)</small>
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Trading Mode *</label>
              <select 
                value={form.defaultMode} 
                onChange={e => setForm({ ...form, defaultMode: e.target.value as any })}
                style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
              >
                <option value="PAPER">Paper Trading (Simulated)</option>
                <option value="LIVE">Live Trading (Real Money)</option>
                <option value="SHADOW">Shadow Mode (Analysis Only)</option>
              </select>
              <small style={{ color: '#666', fontSize: 12 }}>
                <strong>Paper:</strong> Virtual trading for testing • <strong>Live:</strong> Real money trading • <strong>Shadow:</strong> Analysis without trading
              </small>
            </div>

            {error && <div style={{ color: branding.errorColor, padding: 8, background: '#ffe6e6', border: '1px solid #ffcccc', borderRadius: 4 }}>{error}</div>}
            <button type="submit" className="admin-button primary" style={{ padding: 12, fontSize: 16, fontWeight: 500 }}>Create Bot</button>
          </div>
        </form>

        {/* Bots List and Details */}
        <div style={{ background: '#fff', border: `1px solid ${branding.borderColor}`, borderRadius: 8, padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ marginTop: 0 }}>Trading Bots</h3>
            <button onClick={fetchBots} disabled={loading}>Refresh</button>
          </div>
          
          {/* Exchange Data Display */}
          {exchangeData && (
            <div style={{ marginBottom: 16, padding: 12, background: '#f8f9fa', borderRadius: 6, border: `1px solid ${branding.borderColor}` }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Market Data - {exchangeData.symbol}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, fontSize: 14 }}>
                <div>Price: ${exchangeData.last?.toFixed(2) || 'N/A'}</div>
                <div style={{ color: exchangeData.percentage >= 0 ? 'green' : 'red' }}>
                  Change: {exchangeData.percentage?.toFixed(2) || 'N/A'}%
                </div>
                <div>Volume: {exchangeData.volume?.toFixed(0) || 'N/A'}</div>
              </div>
            </div>
          )}

          {/* Mobile Card Layout */}
          {isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {bots.map(b => (
                <div 
                  key={b.id} 
                  style={{ 
                    border: `1px solid ${branding.borderColor}`, 
                    borderRadius: 8, 
                    padding: 16,
                    backgroundColor: 'white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                >
                  {/* Bot Info */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: 8
                    }}>
                      <h4 style={{ margin: 0, fontSize: 16, color: branding.primaryColor }}>
                        {b.name}
                      </h4>
                      <span style={{ 
                        padding: '4px 8px', 
                        borderRadius: 4, 
                        fontSize: 12,
                        fontWeight: 600,
                        backgroundColor: b.status === 'RUNNING' ? '#d4edda' : '#f8d7da',
                        color: b.status === 'RUNNING' ? '#155724' : '#721c24'
                      }}>
                        {b.status}
                      </span>
                    </div>
                    
                    <div style={{ display: 'grid', gap: 6, fontSize: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#666' }}>Symbol:</span>
                        <span style={{ fontWeight: 600 }}>{b.symbol}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#666' }}>Strategy:</span>
                        <span style={{ fontWeight: 600 }}>{b.strategy}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#666' }}>Max Notional:</span>
                        <span style={{ fontWeight: 600 }}>${b.maxNotionalUsd}</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(2, 1fr)', 
                    gap: 8,
                    paddingTop: 12,
                    borderTop: `1px solid ${branding.borderColor}`
                  }}>
                    <button 
                      onClick={() => handleStart(b.id)} 
                      disabled={b.status === 'RUNNING'} 
                      style={{ 
                        fontSize: 13, 
                        padding: '8px 12px',
                        backgroundColor: b.status === 'RUNNING' ? '#ccc' : '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: b.status === 'RUNNING' ? 'not-allowed' : 'pointer',
                        fontWeight: 600
                      }}
                    >
                      ▶ Start
                    </button>
                    <button 
                      onClick={() => handleStop(b.id)} 
                      disabled={b.status !== 'RUNNING'} 
                      style={{ 
                        fontSize: 13, 
                        padding: '8px 12px',
                        backgroundColor: b.status !== 'RUNNING' ? '#ccc' : '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: b.status !== 'RUNNING' ? 'not-allowed' : 'pointer',
                        fontWeight: 600
                      }}
                    >
                      ⏸ Stop
                    </button>
                    <button 
                      onClick={() => setSelectedBot(prev => prev === b.id ? null : b.id)} 
                      style={{ 
                        fontSize: 13, 
                        padding: '8px 12px',
                        backgroundColor: selectedBot === b.id ? branding.primaryColor : 'transparent',
                        color: selectedBot === b.id ? 'white' : branding.primaryColor,
                        border: `1px solid ${branding.primaryColor}`,
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontWeight: 600
                      }}
                    >
                      {selectedBot === b.id ? '▼ Hide' : '▶ Details'}
                    </button>
                    <button 
                      onClick={() => setShowAnalytics({ type: 'bots', id: b.id })}
                      style={{ 
                        fontSize: 13, 
                        padding: '8px 12px',
                        background: '#667eea', 
                        color: 'white', 
                        border: 'none',
                        borderRadius: 6, 
                        cursor: 'pointer',
                        fontWeight: 600
                      }}
                    >
                      📊 Analytics
                    </button>
                    <button 
                      onClick={async () => { 
                        if (window.confirm(`Delete bot "${b.name}"?`)) {
                          await adminApiService.deleteTradingBot(b.id); 
                          fetchBots(); 
                        }
                      }} 
                      style={{ 
                        fontSize: 13, 
                        padding: '8px 12px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontWeight: 600,
                        gridColumn: 'span 2'
                      }}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Desktop Table Layout */
            <div style={{ width: '100%', overflowX: 'visible' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse'
              }}>
            <thead>
              <tr>
                    <th align="left" style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>Name</th>
                    <th align="left" style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>Symbol</th>
                    <th align="left" style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>Strategy</th>
                    <th align="left" style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>Status</th>
                    <th align="left" style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bots.map(b => (
                <tr key={b.id}>
                      <td style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>{b.name}</td>
                      <td style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>{b.symbol}</td>
                      <td style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>{b.strategy}</td>
                      <td style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>
                    <span style={{ 
                      padding: '4px 8px', 
                      borderRadius: 4, 
                      fontSize: 12,
                      backgroundColor: b.status === 'RUNNING' ? '#d4edda' : '#f8d7da',
                      color: b.status === 'RUNNING' ? '#155724' : '#721c24'
                    }}>
                      {b.status}
                    </span>
                  </td>
                      <td style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button onClick={() => handleStart(b.id)} disabled={b.status === 'RUNNING'} style={{ fontSize: 14, padding: '6px 12px' }}>Start</button>
                          <button onClick={() => handleStop(b.id)} disabled={b.status !== 'RUNNING'} style={{ fontSize: 14, padding: '6px 12px' }}>Stop</button>
                          <button onClick={() => setSelectedBot(prev => prev === b.id ? null : b.id)} style={{ fontSize: 14, padding: '6px 12px' }}>
                      {selectedBot === b.id ? 'Hide Details' : 'View Details'}
                    </button>
                    <button 
                      onClick={() => setShowAnalytics({ type: 'bots', id: b.id })}
                            style={{ background: '#667eea', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: 14 }}
                    >
                      Analytics
                    </button>
                          <button onClick={async () => { await adminApiService.deleteTradingBot(b.id); fetchBots(); }} style={{ fontSize: 14, padding: '6px 12px' }}>Delete</button>
                        </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
            </div>
          )}

          {/* Enhanced Bot Details Panel */}
          {selectedBot != null && (
            <div style={{ marginTop: 16, border: `1px solid ${branding.borderColor}`, borderRadius: 8, overflow: 'hidden' }}>
              {/* Tab Navigation */}
              <div style={{ display: 'flex', borderBottom: `1px solid ${branding.borderColor}` }}>
                {['overview', 'metrics', 'trades', 'risk', 'strategy', 'crypto-wallets', 'users'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setBotDetailTab(tab as any)}
                    style={{
                      padding: '12px 16px',
                      border: 'none',
                      background: botDetailTab === tab ? branding.primaryColor : 'transparent',
                      color: botDetailTab === tab ? 'white' : branding.textColor,
                      cursor: 'pointer',
                      textTransform: 'capitalize'
                    }}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div style={{ padding: 16 }}>
                {botDetailTab === 'overview' && (
                  <div>
                    <h4>
                      Live Status - {bots.find(b => b.id === selectedBot)?.name || `Bot ${selectedBot}`}
                    </h4>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', background: '#f8f9fa', padding: 12, borderRadius: 4 }}>
                      {liveStatus ? JSON.stringify(liveStatus, null, 2) : 'No live status available yet'}
                    </pre>
                  </div>
                )}

                {botDetailTab === 'metrics' && botMetrics && (
                  <div>
                    <h4>Trading Metrics</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                      <div>
                        <h5>Performance</h5>
                        <div style={{ display: 'grid', gap: 8 }}>
                          <div>Portfolio Value: ${botMetrics.portfolioValue?.toFixed(2) || 'N/A'}</div>
                          <div>Realized P&L: ${botMetrics.realizedPnl?.toFixed(2) || 'N/A'}</div>
                          <div>Peak Drawdown: ${botMetrics.peakDrawdown?.toFixed(2) || 'N/A'}</div>
                        </div>
                      </div>
                      <div>
                        <h5>Trading Stats</h5>
                        <div style={{ display: 'grid', gap: 8 }}>
                          <div>Total Trades: {botMetrics.tradingStats?.totalTrades || 0}</div>
                          <div>Win Rate: {botMetrics.tradingStats?.winRate?.toFixed(1) || '0.0'}%</div>
                          <div>Total Fees: ${botMetrics.tradingStats?.totalFees?.toFixed(2) || '0.00'}</div>
                          <div>Total Volume: ${botMetrics.tradingStats?.totalVolume?.toFixed(2) || '0.00'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {botDetailTab === 'risk' && botMetrics && (
                  <div>
                    <h4>Risk Metrics</h4>
                    <div style={{ display: 'grid', gap: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                        <span>Current Drawdown:</span>
                        <span style={{ color: botMetrics.riskMetrics?.currentDrawdownPct > 0 ? 'red' : 'green' }}>
                          {botMetrics.riskMetrics?.currentDrawdownPct?.toFixed(2) || '0.00'}%
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                        <span>Max Drawdown:</span>
                        <span style={{ color: botMetrics.riskMetrics?.maxDrawdownPct > 0 ? 'red' : 'green' }}>
                          {botMetrics.riskMetrics?.maxDrawdownPct?.toFixed(2) || '0.00'}%
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                        <span>Max Notional:</span>
                        <span>${bots.find(b => b.id === selectedBot)?.maxNotionalUsd || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                )}

                {botDetailTab === 'trades' && (
                  <div>
                    <h4>Recent Trades</h4>
                    <div style={{ padding: 16, textAlign: 'center', color: '#666' }}>
                      Trade history will be displayed here when available
                    </div>
                  </div>
                )}

                {botDetailTab === 'strategy' && selectedBot && (
                  <SimpleStrategyConfig 
                    botId={selectedBot}
                    botName={bots.find(b => b.id === selectedBot)?.name}
                    botStrategy={bots.find(b => b.id === selectedBot)?.strategy}
                  />
                )}

                {botDetailTab === 'crypto-wallets' && selectedBot && (
                  <div>
                    <h4>Crypto Wallet Allocations (Admin View)</h4>
                    <p style={{ color: '#6c757d', fontSize: '14px', marginBottom: '16px' }}>
                      <strong>Note:</strong> Crypto wallet allocations are managed by retail users. This view shows monitoring information only.
                    </p>
                    
                    {botCryptoAllocations.length > 0 ? (
                      <div>
                        <h5>Current Allocations:</h5>
                        {botCryptoAllocations.map((allocation: any) => (
                          <div key={allocation.id} style={{
                            padding: 12,
                            margin: '8px 0',
                            background: '#f8f9fa',
                            borderRadius: 6,
                            border: '1px solid #dee2e6'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <div style={{ flex: 1 }}>
                                <div><strong>User ID:</strong> {allocation.userId}</div>
                                <div><strong>Wallet:</strong> {allocation.walletAddress || 'N/A'}</div>
                                <div><strong>Blockchain:</strong> {allocation.blockchain || allocation.allocatedCryptoCurrency}</div>
                                <div><strong>Allocated:</strong> {allocation.allocatedCryptoAmount} {allocation.allocatedCryptoCurrency}</div>
                                <div><strong>USD Value:</strong> ${allocation.allocatedUsdValue}</div>
                                <div><strong>Status:</strong> 
                                  <span style={{
                                    padding: '2px 8px',
                                    borderRadius: 4,
                                    fontSize: '12px',
                                    backgroundColor: allocation.allocationStatus === 'ACTIVE' ? '#d4edda' : '#f8d7da',
                                    color: allocation.allocationStatus === 'ACTIVE' ? '#155724' : '#721c24',
                                    marginLeft: 8
                                  }}>
                                    {allocation.allocationStatus}
                                  </span>
                                </div>
                                {allocation.notes && <div><strong>Notes:</strong> {allocation.notes}</div>}
                                <div><strong>Created:</strong> {new Date(allocation.createdAt).toLocaleString()}</div>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <button 
                                  style={{
                                    padding: '4px 8px',
                                    background: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                  }}
                                  onClick={() => {
                                    if (window.confirm('Are you sure you want to cancel this allocation?')) {
                                      // Add cancel allocation logic here
                                      console.log('Cancel allocation:', allocation.id);
                                    }
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{
                        padding: 20,
                        textAlign: 'center',
                        background: '#f8f9fa',
                        borderRadius: 8,
                        border: '1px solid #dee2e6'
                      }}>
                        <p style={{ margin: 0, color: '#6c757d' }}>No crypto wallets allocated to this bot yet.</p>
                        <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#6c757d' }}>
                          Retail users can allocate their crypto wallets to this bot from the Trading Bots page.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {botDetailTab === 'users' && (
                  <div>
                    <h4>Retail Users Using This Bot</h4>
                    {usersData.length === 0 ? (
                      <div style={{ padding: 16, textAlign: 'center', color: '#666' }}>No users found for this bot yet</div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ background: '#f8f9fa' }}>
                              <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e9ecef' }}>User</th>
                              <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e9ecef' }}>Allocated</th>
                              <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e9ecef' }}>Current Value</th>
                              <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e9ecef' }}>P&L</th>
                              <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e9ecef' }}>P&L %</th>
                              <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e9ecef' }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {usersData.map((u: any) => (
                              <tr key={u.userId}>
                                <td style={{ padding: 8, borderBottom: '1px solid #f1f3f5' }}>
                                  <div style={{ fontWeight: 600 }}>{u.username || `User ${u.userId}`}</div>
                                  <div style={{ color: '#666', fontSize: 12 }}>{u.email}</div>
                                </td>
                                <td style={{ padding: 8, borderBottom: '1px solid #f1f3f5' }}>${(u.allocatedAmount || 0).toFixed(2)}</td>
                                <td style={{ padding: 8, borderBottom: '1px solid #f1f3f5' }}>${(u.currentValue || 0).toFixed(2)}</td>
                                <td style={{ padding: 8, borderBottom: '1px solid #f1f3f5', color: (u.totalPnl||0) >= 0 ? 'green' : 'red' }}>${(u.totalPnl || 0).toFixed(2)}</td>
                                <td style={{ padding: 8, borderBottom: '1px solid #f1f3f5', color: (u.pnlPercentage||0) >= 0 ? 'green' : 'red' }}>{(u.pnlPercentage || 0).toFixed(2)}%</td>
                                <td style={{ padding: 8, borderBottom: '1px solid #f1f3f5' }}>{u.status || 'ACTIVE'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        </div>
      ) : canViewStrategiesTab ? (
        <AdminTradingStrategies onShowAnalytics={(strategyId: number) => setShowAnalytics({ type: 'strategies', id: strategyId })} />
      ) : (
        <div
          style={{
            border: `1px solid ${branding.borderColor}`,
            borderRadius: 8,
            background: '#fff',
            padding: 24,
            color: branding.textColor
          }}
        >
          You don't have permission to view this tab.
        </div>
      )}

      {/* Analytics Modal */}
      {showAnalytics && (
        <div 
          key={`analytics-modal-container-${showAnalytics.type}-${showAnalytics.id}`}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: window.innerWidth <= 768 ? 'flex-start' : 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: window.innerWidth <= 768 ? '0' : '20px',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}
          onClick={(e) => {
            // Close modal when clicking backdrop
            // CRITICAL: Add logging to track when/why modal closes
            if (e.target === e.currentTarget) {
              console.log('TradingBotsPanel: Backdrop clicked - closing modal', {
                showAnalytics,
                timestamp: new Date().toISOString()
              });
              setShowAnalytics(null);
              // Clear from localStorage when explicitly closed
              localStorage.removeItem('tradingBotsPanelShowAnalytics');
            }
          }}
        >
          <div 
            onClick={(e) => {
              // CRITICAL: Prevent clicks inside modal from closing it
              e.stopPropagation();
            }}
            style={{
              backgroundColor: 'white',
              borderRadius: window.innerWidth <= 768 ? '0' : '10px',
              width: window.innerWidth <= 768 ? '100%' : 'calc(100% - 48px)',
              maxWidth: window.innerWidth <= 768 ? '100%' : '920px',
              maxHeight: window.innerWidth <= 768 ? '100%' : '90vh',
              height: window.innerWidth <= 768 ? '100%' : 'auto',
              overflow: 'auto',
              boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
              margin: window.innerWidth <= 768 ? '0' : 'auto',
              boxSizing: 'border-box',
            }}
          >
            <AdminAnalyticsTabs
              key={`${showAnalytics.type}-${showAnalytics.id}`}
              type={showAnalytics.type}
              selectedId={showAnalytics.id}
              onClose={() => {
                console.log('TradingBotsPanel: onClose called from AdminAnalyticsTabs', {
                  showAnalytics,
                  timestamp: new Date().toISOString()
                });
                setShowAnalytics(null);
                // Clear from localStorage when explicitly closed
                localStorage.removeItem('tradingBotsPanelShowAnalytics');
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TradingBotsPanel;
